<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        header="สร้างถังดับเพลิงเปล่า"
        :modal="true"
        :closable="true"
        :style="{ width: '600px' }"
        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    >
        <div class="generate-extinguisher-content">
            <div class="p-fluid">
                <!-- เลือกประเภทถัง -->
                <div class="p-field">
                    <label for="extinguisher-type">ประเภทถังดับเพลิง <span style="color: red">*</span></label>
                    <Dropdown
                        id="extinguisher-type"
                        v-model="selectedType"
                        :options="typeList"
                        optionLabel="name"
                        optionValue="value"
                        placeholder="เลือกประเภทถังดับเพลิง"
                        :class="isValidSelectType"
                    />
                    <small v-if="submitted && !selectedType" class="p-error">กรุณาเลือกประเภทถังดับเพลิง</small>
                </div>

                <!-- จำนวนถัง -->
                <div class="p-field">
                    <label for="quantity">จำนวนถัง <span style="color: red">*</span></label>
                    <InputNumber id="quantity" v-model="quantity" :min="1" :max="10000" placeholder="จำนวนถัง" :class="isValidQuantity" />
                    <small v-if="submitted && (!quantity || quantity < 1)" class="p-error">กรุณาระบุจำนวนถัง (1-10000)</small>
                </div>

                <!-- สถานะเริ่มต้น -->
                <div class="p-field">
                    <label for="initial-status">สถานะเริ่มต้น</label>
                    <Dropdown
                        id="initial-status"
                        v-model="initialStatus"
                        :options="statusOptions"
                        optionLabel="name"
                        optionValue="value"
                        placeholder="เลือกสถานะเริ่มต้น"
                    />
                </div>

                <!-- สถานะถังดับเพลิง -->
                <div class="p-field">
                    <label for="extinguisher-status">สถานะถังดับเพลิง</label>
                    <Dropdown
                        id="extinguisher-status"
                        v-model="extinguisherStatus"
                        :options="extinguisherStatusOptions"
                        optionLabel="name"
                        optionValue="value"
                        placeholder="เลือกสถานะถังดับเพลิง"
                    />
                </div>

                <!-- ตัวอย่าง Code ที่จะสร้าง -->
                <div class="p-field" v-if="selectedType && quantity && previewCodes.length > 0">
                    <label>ตัวอย่าง Code ที่จะสร้าง:</label>
                    <div class="code-preview">
                        <span v-for="(code, index) in previewCodes" :key="index" class="code-item">
                            {{ code }}
                        </span>
                        <span v-if="quantity > 5" class="more-codes"> ... และอีก {{ quantity - 5 }} รายการ </span>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="p-d-flex p-jc-end">
                <Button label="ยกเลิก" icon="pi pi-times" class="p-button-text p-mr-2" @click="closeModal" />
                <Button label="สร้างถังดับเพลิง" icon="pi pi-check" :loading="loading" @click="generateExtinguishers" />
            </div>
        </template>

        <!-- Result Dialog -->
        <Dialog v-model:visible="showResult" header="ผลการสร้างถังดับเพลิง" :modal="true" :closable="false" :style="{ width: '500px' }">
            <div class="result-content">
                <div class="p-d-flex p-ai-center p-mb-3">
                    <i class="pi pi-check-circle p-mr-2" style="color: #4caf50; font-size: 2rem"></i>
                    <div>
                        <h4 class="p-m-0">สร้างถังดับเพลิงสำเร็จ!</h4>
                        <p class="p-m-0">สร้างถังดับเพลิงเปล่าจำนวน {{ generatedCount }} ถัง</p>
                    </div>
                </div>

                <div class="generated-codes">
                    <h5>รหัสถังที่สร้าง:</h5>
                    <ul>
                        <li v-for="code in generatedCodes" :key="code">{{ code }}</li>
                    </ul>
                </div>
            </div>

            <template #footer>
                <Button label="ตกลง" icon="pi pi-check" @click="closeResult" />
            </template>
        </Dialog>
    </Dialog>
</template>

<script>
export default {
    name: 'GenerateExtinguisherModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:visible', 'generated'],
    data() {
        return {
            selectedType: null,
            quantity: 1,
            initialStatus: 'ยังไม่ตรวจสอบ',
            extinguisherStatus: 'ชำรุด',
            description: '',
            submitted: false,
            loading: false,
            showResult: false,
            generatedCount: 0,
            generatedCodes: [],
            previewCodes: [], // เก็บ preview codes

            typeList: [
                { name: 'ถังดับเพลิงชนิดโฟม (Foam Extinguishers)', value: 'F' },
                { name: 'ถังดับเพลิงสูตรน้ำ (Low Pressure Water Mist)', value: 'L' },
                { name: 'ถังดับเพลิงชนิดผงเคมีแห้ง (Dry Chemical)', value: 'D' },
                { name: 'ถังดับเพลิงชนิดก๊าซคาร์บอนไดออกไซด์ ( CO2 )', value: 'C' },
                { name: 'ถังดับเพลิงชนิดน้ำ (Water Extinguishers)', value: 'W' }
            ],

            statusOptions: [
                { name: 'ยังไม่ตรวจสอบ', value: 'ยังไม่ตรวจสอบ' },
                { name: 'ตรวจสอบแล้ว', value: 'ตรวจสอบแล้ว' },
                { name: 'รอตรวจสอบ', value: 'รอตรวจสอบ' }
            ],

            extinguisherStatusOptions: [
                { name: 'ชำรุด', value: 'ชำรุด' },
                { name: 'ปกติ', value: 'ปกติ' },
                { name: 'ต้องซ่อมแซม', value: 'ต้องซ่อมแซม' },
                { name: 'ไม่ได้ใช้งาน', value: 'ไม่ได้ใช้งาน' }
            ]
        };
    },

    watch: {
        visible(newVal) {
            if (newVal) {
                this.resetForm();
            }
        },
        selectedType() {
            this.updatePreviewCodes();
        },
        quantity() {
            this.updatePreviewCodes();
        }
    },

    computed: {
        isValidSelectType() {
            return { 'p-invalid': this.submitted && !this.selectedType };
        },
        isValidQuantity() {
            return { 'p-invalid': this.submitted && (!this.quantity || this.quantity < 1) };
        }
    },

    methods: {
        // เรียก API สร้าง code เดี่ยว
        async generateCodes(codeDescription, quantity) {
            try {
                const response = await this.axios.post(`${this.$url_service}/initial/extinguisher`, {
                    body: {
                        topic: 'generatecode-extinguisher-new',
                        codeDescription: codeDescription,
                        quantity: quantity
                    }
                });

                if (response.status == 200 && response.data.codes) {
                    return response.data.codes;
                } else {
                    throw new Error('ไม่สามารถสร้างรหัสได้');
                }
            } catch (error) {
                console.error('Error generating codes:', error);
                throw error;
            }
        },

        // อัพเดท preview codes
        async updatePreviewCodes() {
            if (this.selectedType && this.quantity > 0) {
                try {
                    const previewCount = Math.min(this.quantity, 5);
                    const previewCodes = await this.generateCodes(this.selectedType, previewCount);
                    this.previewCodes = previewCodes;
                } catch (error) {
                    console.error('Error generating preview codes:', error);
                    // fallback ใช้ random code
                    this.previewCodes = [];
                    for (let i = 0; i < Math.min(this.quantity, 5); i++) {
                        const randomNum = Math.floor(Math.random() * 100000000);
                        this.previewCodes.push(`${this.selectedType}${String(randomNum + i).padStart(8, '0')}`);
                    }
                }
            } else {
                this.previewCodes = [];
            }
        },

        getUser() {
            const getUser = this.decodeJWT(localStorage.getItem('token'));
            if (typeof getUser == 'string') {
                return JSON.parse(getUser);
            }
            return getUser;
        },

        // สร้างถังดับเพลิง
        async generateExtinguishers() {
            this.submitted = true;

            // Validation
            if (!this.selectedType || !this.quantity || this.quantity < 1) {
                this.$fire({
                    title: 'ข้อมูลไม่ครบถ้วน',
                    type: 'error',
                    timer: 3000,
                    detail: 'กรุณากรอกข้อมูลให้ครบถ้วน',
                    confirmButtonColor: 'red'
                });

                return;
            }

            this.loading = true;

            try {
                // สร้าง codes สำหรับถังทุกใบก่อน
                const generatedCodes = await this.generateCodes(this.selectedType, this.quantity);
                const payload = {
                    topic: 'generate-extinguisher',
                    type: this.selectedType,
                    quantity: this.quantity,
                    initialStatus: this.initialStatus,
                    extinguisherStatus: this.extinguisherStatus,
                    description: this.description,
                    codes: generatedCodes, // ส่ง codes ที่สร้างแล้วไป
                    userData: this.getUser()
                };

                const response = await this.axios.post(`${this.$url_service}/initial/extinguisher`, {
                    body: payload
                });

                if (response.status == 200) {
                    this.generatedCount = response.data.generatedCount;
                    this.generatedCodes = response.data.generatedCodes || generatedCodes;
                    this.showResult = true;

                    // Emit event เพื่อให้ parent component รู้ว่ามีการสร้างถังใหม่
                    this.$emit('generated', {
                        count: this.generatedCount,
                        codes: this.generatedCodes,
                        type: this.selectedType
                    });

                    this.$fire({
                        title: 'เพิ่มถังเปล่าสำเร็จ',
                        type: 'success',
                        timer: 3000,
                        detail: `สร้างถังดับเพลิงเปล่าจำนวน ${this.generatedCount} ถัง`,
                        confirmButtonColor: '#28B463'
                    });
                } else {
                    throw new Error(response.data.message || 'เกิดข้อผิดพลาด');
                }
            } catch (error) {
                console.error('Error generating extinguishers:', error);

                this.$fire({
                    title: 'เกิดข้อผิดพลาด',
                    type: 'error',
                    timer: 3000,
                    detail: error.message || 'ไม่สามารถสร้างถังดับเพลิงได้',
                    confirmButtonColor: 'red'
                });
            } finally {
                this.loading = false;
                this.$emit('update:visible', false)
            }
        },

        // รีเซ็ตฟอร์ม
        resetForm() {
            this.selectedType = null;
            this.quantity = 1;
            this.initialStatus = 'ยังไม่ตรวจสอบ';
            this.extinguisherStatus = 'ชำรุด';
            this.description = '';
            this.submitted = false;
            this.previewCodes = []; // เคลียร์ preview codes
        },

        // ปิด modal
        closeModal() {
            this.resetForm();
            this.$emit('update:visible', false);
        },

        // ปิด result dialog
        closeResult() {
            this.showResult = false;
            this.generatedCount = 0;
            this.generatedCodes = [];
            // ปิด modal หลักด้วย
            this.closeModal();
        }
    }
};
</script>

<style scoped>
.generate-extinguisher-content {
    padding: 0;
}

.code-preview {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0.75rem;
    font-family: monospace;
}

.code-item {
    display: inline-block;
    background-color: #e9ecef;
    padding: 0.25rem 0.5rem;
    margin: 0.25rem;
    border-radius: 3px;
    font-size: 0.875rem;
}

.more-codes {
    color: #6c757d;
    font-style: italic;
    margin-left: 0.5rem;
}

.result-content .generated-codes {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-top: 1rem;
}

.result-content .generated-codes ul {
    max-height: 200px;
    overflow-y: auto;
    margin: 0;
    padding-left: 1.5rem;
}

.result-content .generated-codes li {
    font-family: monospace;
    padding: 0.25rem 0;
}
</style>
